import { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, MapPin, ChevronRight } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

interface EliminationScheduleMatch {
  phase: string;
  court: number;
  startTime: string;
  endTime: string;
  category: string;
  participants: string[];
  date: string;
  status?: 'scheduled' | 'completed';
  score?: string;
  winnerTeam?: number | null;
  assignedCourt?: string;
  matchNumber?: number;
  courtHidden?: boolean; // organizer hid the pre-assigned court from participants
}

interface EliminationBracketTreeProps {
  eliminationMatches: EliminationScheduleMatch[];
  player1Name?: string | null;
  player2Name?: string | null;
  nextUpCourt?: string | null;
  /** Normalized phase key from notification: 'octavos' | 'cuartos' | 'semifinales' | 'final' | 'eliminacion' */
  nextUpPhase?: string | null;
}

type PhaseKey = 'octavos' | 'cuartos' | 'semifinales' | 'final';

const PHASE_MAP: Record<string, PhaseKey> = {
  'Octavos de Final': 'octavos',
  'Cuartos de Final': 'cuartos',
  'Semifinales': 'semifinales',
  'Semifinal': 'semifinales',
  'Final': 'final',
};

const PHASE_ORDER: PhaseKey[] = ['octavos', 'cuartos', 'semifinales', 'final'];

const normalize = (name: string) =>
  name?.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s*[-/]\s*/g, ' / ').replace(/\s+/g, ' ') || '';

function lower_is_group(phase: string): boolean {
  return phase?.toLowerCase().startsWith('grupo') || false;
}

const EliminationBracketTree = ({
  eliminationMatches,
  player1Name,
  player2Name,
  nextUpCourt,
  nextUpPhase,
}: EliminationBracketTreeProps) => {
  const { t, language } = useLanguage();

  const userFullName = useMemo(() => {
    if (!player1Name) return '';
    return player2Name
      ? normalize(`${player1Name} / ${player2Name}`)
      : normalize(player1Name);
  }, [player1Name, player2Name]);

  const phaseGroups = useMemo(() => {
    const groups: Record<PhaseKey, EliminationScheduleMatch[]> = {
      octavos: [], cuartos: [], semifinales: [], final: [],
    };
    eliminationMatches.forEach(match => {
      const phase = match.phase;
      let key = PHASE_MAP[phase];
      if (!key) {
        const lower = phase?.toLowerCase() || '';
        if (lower.includes('octavo') || lower === 'r16' || lower === 'oct') key = 'octavos';
        else if (lower.includes('cuarto') || lower === 'qf') key = 'cuartos';
        else if (lower.includes('semi') || lower === 'sf') key = 'semifinales';
        else if (lower.includes('final')) key = 'final';
      }
      if (key && !lower_is_group(phase)) {
        groups[key].push(match);
      }
    });
    for (const key of PHASE_ORDER) {
      groups[key].sort((a, b) => (a.matchNumber || 0) - (b.matchNumber || 0));
    }
    return groups;
  }, [eliminationMatches]);

  const activePhases = useMemo(() =>
    PHASE_ORDER.filter(p => phaseGroups[p].length > 0),
  [phaseGroups]);

  const [selectedPhase, setSelectedPhase] = useState<PhaseKey | null>(null);

  const currentPhase = selectedPhase && activePhases.includes(selectedPhase)
    ? selectedPhase
    : activePhases[0] || null;

  if (activePhases.length === 0) {
    return (
      <div className="text-center text-muted-foreground py-6">
        {t('tournament.noElimMatchesYet')}
      </div>
    );
  }

  const isUserInMatch = (match: EliminationScheduleMatch) => {
    if (!userFullName) return false;
    return match.participants?.some(p => normalize(p) === userFullName) || false;
  };

  const getPhaseTitle = (key: PhaseKey) => {
    const titles: Record<PhaseKey, Record<string, string>> = {
      octavos: { es: 'Octavos', en: 'R16' },
      cuartos: { es: 'Cuartos', en: 'QF' },
      semifinales: { es: 'Semis', en: 'SF' },
      final: { es: 'Final', en: 'Final' },
    };
    return titles[key][language] || key;
  };

  const getPhaseFullTitle = (key: PhaseKey) => {
    const titles: Record<PhaseKey, Record<string, string>> = {
      octavos: { es: 'Octavos de Final', en: 'Round of 16' },
      cuartos: { es: 'Cuartos de Final', en: 'Quarterfinals' },
      semifinales: { es: 'Semifinales', en: 'Semifinals' },
      final: { es: 'Final', en: 'Final' },
    };
    return titles[key][language] || key;
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-');
    if (!y || !m || !d) return dateStr;
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    return date.toLocaleDateString(language === 'es' ? 'es-MX' : 'en-US', { day: 'numeric', month: 'short' });
  };

  const userFirstPendingPhase = (() => {
    for (const phaseKey of activePhases) {
      for (const match of phaseGroups[phaseKey]) {
        if (!isUserInMatch(match)) continue;
        const finished = !!(match.score || match.winnerTeam);
        if (!finished) return phaseKey;
      }
    }
    return null;
  })();

  const truncate = (name: string, max: number) => name.length > max ? name.slice(0, max) + '…' : name;

  const formatCourt = (raw: string | null | undefined): string | null => {
    if (!raw) return null;
    const trimmed = String(raw).trim();
    if (!trimmed) return null;
    if (/^cancha\s/i.test(trimmed)) return trimmed;
    if (/^C\d+$/i.test(trimmed)) return `Cancha ${trimmed.slice(1)}`;
    return `Cancha ${trimmed}`;
  };

  const renderMatchCard = (match: EliminationScheduleMatch, phaseKey: PhaseKey, index: number) => {
    const team1 = match.participants?.[0] || 'TBD';
    const team2 = match.participants?.[1] || 'TBD';
    const isFinished = !!(match.score || match.winnerTeam);
    const userIn = isUserInMatch(match);
    const assignedCourt = formatCourt(match.assignedCourt);
    const formattedNextUpCourt = formatCourt(nextUpCourt);
    // Gate the next-up banner by phase
    const phaseMatchesNotif = !nextUpPhase
      || nextUpPhase === 'eliminacion'
      || nextUpPhase === phaseKey;
    const isUserNextUp = userIn && !isFinished && !!formattedNextUpCourt && phaseMatchesNotif;
    const footerCourt = isUserNextUp && formattedNextUpCourt
      ? formattedNextUpCourt
      : assignedCourt
        ? assignedCourt
        // Pre-assigned court is suppressed when the organizer hid courts.
        // Live assignments (assignedCourt / next-up) above still show.
        : (!match.courtHidden && match.court ? `Cancha ${match.court}` : null);

    const isUserTeam1 = userFullName && normalize(team1) === userFullName;
    const isUserTeam2 = userFullName && normalize(team2) === userFullName;

    const userWon = isFinished && (
      (isUserTeam1 && match.winnerTeam === 1) || (isUserTeam2 && match.winnerTeam === 2)
    );
    const userLost = isFinished && userIn && !userWon;

    const statusText = isFinished
      ? (language === 'es' ? 'Finalizado' : 'Finished')
      : isUserNextUp
        ? (language === 'es' ? `Próximo en ${formattedNextUpCourt}` : `Next on ${formattedNextUpCourt}`)
        : assignedCourt
          ? assignedCourt
          : (language === 'es' ? 'Sin asignar' : 'Unassigned');
    const statusBg = isFinished
      ? 'bg-muted/60 text-muted-foreground'
      : isUserNextUp
        ? 'bg-blue-50 text-blue-700'
        : assignedCourt
          ? 'bg-green-50 text-green-700'
          : 'bg-muted/60 text-muted-foreground';

    return (
      <div
        key={`${phaseKey}-${index}`}
        className={`rounded-xl border overflow-hidden ${
          userIn
            ? 'border-primary/40 ring-1 ring-primary/20'
            : 'border-border'
        } bg-card`}
      >
        {/* Status bar */}
        <div className={`px-3 py-1.5 text-xs font-medium text-center border-b ${statusBg}`}>
          {isFinished ? '✓ ' : isUserNextUp ? <><MapPin className="inline h-3.5 w-3.5 mr-1" /></> : assignedCourt ? <><MapPin className="inline h-3.5 w-3.5 mr-1" /></> : ''}{statusText}
        </div>

        {/* Body */}
        <div className="px-3 py-3 space-y-2">
          {userIn && isFinished && (
            <div className="flex justify-end">
              <Badge variant="outline" className={`text-[10px] px-2 py-0.5 ${
                userWon ? 'bg-green-50 border-green-300 text-green-700' : 'bg-red-50 border-red-300 text-red-700'
              }`}>
                {userWon
                  ? (language === 'es' ? '✓ Ganaste' : '✓ Won')
                  : (language === 'es' ? '✗ Perdiste' : '✗ Lost')}
              </Badge>
            </div>
          )}

          {/* Team 1 */}
          <p className={`text-sm text-center ${
            isUserTeam1 ? 'font-bold text-primary' :
            isFinished && match.winnerTeam === 1 ? 'font-bold text-foreground' :
            isFinished && match.winnerTeam === 2 ? 'text-muted-foreground line-through' :
            'font-medium text-foreground'
          }`}>
            {team1}
          </p>

          {/* Score or VS */}
          <p className={`text-center font-bold ${isFinished ? 'text-base text-foreground' : 'text-xs text-muted-foreground'}`}>
            {isFinished && match.score ? match.score : 'VS'}
          </p>

          {/* Team 2 */}
          <p className={`text-sm text-center ${
            isUserTeam2 ? 'font-bold text-primary' :
            isFinished && match.winnerTeam === 2 ? 'font-bold text-foreground' :
            isFinished && match.winnerTeam === 1 ? 'text-muted-foreground line-through' :
            'font-medium text-foreground'
          }`}>
            {team2}
          </p>
        </div>

        {/* Footer */}
        {(match.date || match.startTime || footerCourt) && (
          <div className="px-3 py-2 border-t border-border/50 flex items-center justify-center gap-3 text-xs text-muted-foreground">
            {match.date && (
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {formatDate(match.date)}
              </span>
            )}
            {match.startTime && (
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {match.startTime}
              </span>
            )}
            {footerCourt && (
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {footerCourt}
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  // Group matches into pairs
  const currentMatches = currentPhase ? phaseGroups[currentPhase] : [];
  const pairs: EliminationScheduleMatch[][] = [];
  for (let i = 0; i < currentMatches.length; i += 2) {
    pairs.push(currentMatches.slice(i, i + 2));
  }

  return (
    <div className="w-full">
      {/* Phase selector pills */}
      <div className="flex gap-2 overflow-x-auto pb-3 px-2 scrollbar-hide">
        {activePhases.map((phaseKey) => (
          <button
            key={phaseKey}
            onClick={() => setSelectedPhase(phaseKey)}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors border ${
              currentPhase === phaseKey
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-muted/50 text-muted-foreground border-border hover:bg-muted'
            }`}
          >
            {phaseKey === 'final' && '🏆 '}
            {getPhaseTitle(phaseKey)}
          </button>
        ))}
      </div>

      {/* Phase title */}
      {currentPhase && (
        <h3 className="text-center text-sm font-semibold text-foreground mb-3">
          {currentPhase === 'final' && '🏆 '}
          {getPhaseFullTitle(currentPhase)}
        </h3>
      )}

      {/* Match pairs with right-side connector */}
      <div className="flex flex-col gap-6 px-2">
        {pairs.map((pair, pairIdx) => (
          <div key={pairIdx} className="flex items-stretch">
            {/* Cards column */}
            <div className="flex-1 flex flex-col gap-1.5 min-w-0">
              {pair.map((match, idx) =>
                renderMatchCard(match, currentPhase!, pairIdx * 2 + idx)
              )}
            </div>

            {/* Right-side connector (only if pair has 2 matches) */}
            {pair.length === 2 && (
              <div className="flex-shrink-0 w-6 flex items-center justify-center relative">
                <svg width="24" height="100%" viewBox="0 0 24 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full text-muted-foreground/30">
                  <line x1="12" y1="0" x2="12" y2="100" stroke="currentColor" strokeWidth="1.5" />
                </svg>
                <ChevronRight className="h-4 w-4 text-muted-foreground/60 z-10 bg-background rounded-full" />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default EliminationBracketTree;
